import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { KeyIcon } from '@heroicons/react/24/outline';
import { authApi } from '../services/api';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user is already authenticated (e.g., after Azure AD redirect)
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const user = await authApi.getCurrentUser();
        if (user && !user.isNewUser) {
          // User is authenticated, redirect to dashboard or intended destination
          // Check for redirect URL in query params (from Azure AD) or location state
          const urlParams = new URLSearchParams(window.location.search);
          const redirectUrl = urlParams.get('post_login_redirect_uri') || 
                            (location.state as any)?.from?.pathname || 
                            '/';
          // Clean up the URL by removing query params
          window.history.replaceState({}, '', '/login');
          navigate(redirectUrl, { replace: true });
        }
      } catch (error) {
        // Not authenticated, stay on login page
        // This is expected for unauthenticated users
      }
    };

    // Small delay to allow Azure Static Web Apps to process auth token
    const timer = setTimeout(checkAuth, 100);
    return () => clearTimeout(timer);
  }, [navigate, location]);

  const handleLogin = () => {
    // Always redirect to root after login - the app will handle routing
    const redirectUrl = '/';
    const loginUrl = authApi.getLoginUrl(redirectUrl);
    window.location.href = loginUrl;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 via-white to-secondary-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center">
            <div className="w-20 h-20 bg-primary-600 rounded-2xl flex items-center justify-center shadow-lg">
              <KeyIcon className="w-12 h-12 text-white" />
            </div>
          </div>
          <h2 className="mt-6 text-4xl font-bold text-secondary-900">KeyKeeper</h2>
        </div>

        <div className="mt-8 bg-white rounded-xl shadow-lg p-8 border border-secondary-200">
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-secondary-900 mb-2">
                Welcome to KeyKeeper
              </h3>
            </div>

            <button
              onClick={handleLogin}
              className="w-full flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors shadow-md"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              Sign in
            </button>

            <div className="mt-6 pt-6 border-t border-secondary-200">
              <p className="text-xs text-secondary-500 text-center">
                By signing in, you agree to use KeyKeeper in accordance with your organization's policies.
              </p>
            </div>
          </div>
        </div>

        <div className="text-center">
          <p className="text-sm text-secondary-500">
            Need help? Contact your system administrator.
          </p>
        </div>
      </div>
    </div>
  );
}
